import type {
  SpreadsheetExcelIoWarning,
  SpreadsheetExcelOoxmlArtifactManifestEntry,
} from './spreadsheetEngineProtocol'

type UnsupportedArtifactWarningClass = {
  code: string
  label: string
  message: string
}

export type SpreadsheetExcelIoWarningPresentation = {
  code: string
  severity: SpreadsheetExcelIoWarning['severity']
  title: string
  detail: string
  scope?: string | null
}

export type SpreadsheetExcelIoWarningHistoryEntry = {
  operation: 'import' | 'export'
  file_name: string
  created_at_ms: number
  warning_count: number
  warnings: SpreadsheetExcelIoWarningPresentation[]
}

export const SPREADSHEET_EXCEL_IO_WARNING_HISTORY_DOC_MAP = 'spreadsheet_excel_io_warning_history' as const
export const SPREADSHEET_EXCEL_IO_WARNING_HISTORY_LIMIT = 20

const WARNING_CLASSES: Record<string, UnsupportedArtifactWarningClass> = {
  charts: {
    code: 'xlsx-unsupported-chart-visuals-dropped',
    label: 'Excel chart visuals',
    message: 'Excel chart visuals that cannot map to native range charts were dropped; workbook data, formulas, and modeled formatting still import/export.',
  },
  drawings_media: {
    code: 'xlsx-unsupported-drawings-media-dropped',
    label: 'drawings/images',
    message: 'Excel drawings, images, VML shapes, or drawing anchors outside the native range-chart model were dropped.',
  },
  embedded_objects: {
    code: 'xlsx-unsupported-embedded-objects-dropped',
    label: 'embedded objects',
    message: 'Embedded OLE objects or embedded package payloads were dropped from the user-facing workbook flow.',
  },
  pivots: {
    code: 'xlsx-unsupported-pivots-dropped',
    label: 'pivot artifacts',
    message: 'Excel pivot caches or pivot tables were dropped until FuseGrid has an engine-owned pivot artifact model.',
  },
  slicers_timelines: {
    code: 'xlsx-unsupported-slicers-timelines-dropped',
    label: 'slicers/timelines',
    message: 'Excel slicers or timelines were dropped until FuseGrid has an engine-owned filter-control model.',
  },
  custom_xml: {
    code: 'xlsx-unsupported-custom-xml-dropped',
    label: 'custom XML',
    message: 'Arbitrary custom XML package parts were dropped from the user-facing workbook flow.',
  },
  external_data: {
    code: 'xlsx-unsupported-external-data-dropped',
    label: 'external data links/connections',
    message: 'External workbook links, connections, or query tables were dropped until they map to explicit FuseGrid data-source semantics.',
  },
  macros: {
    code: 'xlsx-unsupported-macros-dropped',
    label: 'macros',
    message: 'VBA macro payloads were dropped; macros are not executed or preserved in the current user-facing import/export flow.',
  },
  advanced_ooxml: {
    code: 'xlsx-unsupported-advanced-ooxml-dropped',
    label: 'advanced OOXML',
    message: 'Unsupported advanced OOXML package parts were dropped from the user-facing workbook flow.',
  },
}

export const SPREADSHEET_EXCEL_IO_UNSUPPORTED_ARTIFACT_WARNING_CODES = Object.values(WARNING_CLASSES)
  .map((warningClass) => warningClass.code)

export const SPREADSHEET_EXCEL_IO_NATIVE_CHART_WARNING_CODES = [
  'xlsx-chart-import-partial',
  'xlsx-chart-unsupported',
  'xlsx-chart-source-unresolved',
] as const

const artifactText = (artifact: SpreadsheetExcelOoxmlArtifactManifestEntry): string => [
  artifact.part_name,
  artifact.content_type ?? '',
  artifact.relationship_type ?? '',
  artifact.relationship_target ?? '',
  ...(artifact.relationships ?? []).flatMap((relationship) => [
    relationship.type,
    relationship.target,
  ]),
].join(' ').toLowerCase()

export const classifySpreadsheetExcelUnsupportedArtifact = (
  artifact: SpreadsheetExcelOoxmlArtifactManifestEntry,
): keyof typeof WARNING_CLASSES => {
  const text = artifactText(artifact)
  if (text.includes('vbaproject') || text.includes('vba project')) return 'macros'
  if (text.includes('pivotcache') || text.includes('pivottable')) return 'pivots'
  if (text.includes('slicer') || text.includes('timeline')) return 'slicers_timelines'
  if (text.includes('customxml')) return 'custom_xml'
  if (text.includes('externallink') || text.includes('connections') || text.includes('querytable')) return 'external_data'
  if (text.includes('oleobject') || text.includes('/embeddings/') || text.includes('relationship/package')) return 'embedded_objects'
  if (text.includes('/charts/') || text.includes('drawingml.chart') || text.endsWith('/chart')) return 'charts'
  if (
    text.includes('/drawings/') ||
    text.includes('/media/') ||
    text.includes('vml') ||
    text.includes('relationship/image') ||
    text.includes('relationship/drawing') ||
    text.includes('chartusershapes')
  ) return 'drawings_media'
  return 'advanced_ooxml'
}

export const markExcelArtifactsDroppedForCurrentUiScope = (
  artifacts: SpreadsheetExcelOoxmlArtifactManifestEntry[] | undefined,
): SpreadsheetExcelOoxmlArtifactManifestEntry[] | undefined => {
  if (!Array.isArray(artifacts) || artifacts.length === 0) return undefined
  return artifacts.map((artifact) => ({
    ...artifact,
    payload_base64: null,
    preservation: 'dropped',
  }))
}

export const buildExcelAdvancedArtifactDropWarning = (
  artifacts: SpreadsheetExcelOoxmlArtifactManifestEntry[] | undefined,
): SpreadsheetExcelIoWarning[] => {
  if (!Array.isArray(artifacts) || artifacts.length === 0) return []

  const byClass = new Map<keyof typeof WARNING_CLASSES, SpreadsheetExcelOoxmlArtifactManifestEntry[]>()
  for (const artifact of artifacts) {
    const artifactClass = classifySpreadsheetExcelUnsupportedArtifact(artifact)
    const entries = byClass.get(artifactClass) ?? []
    entries.push(artifact)
    byClass.set(artifactClass, entries)
  }

  return Array.from(byClass.entries())
    .sort(([a], [b]) => WARNING_CLASSES[a].code.localeCompare(WARNING_CLASSES[b].code))
    .map(([artifactClass, entries]) => {
      const warningClass = WARNING_CLASSES[artifactClass]
      const examples = entries
        .map((artifact) => artifact.part_name)
        .filter((partName) => partName.trim().length > 0)
        .slice(0, 3)
      const exampleText = examples.length > 0 ? ` Example part${examples.length === 1 ? '' : 's'}: ${examples.join(', ')}.` : ''
      return {
        code: warningClass.code,
        severity: 'warning',
        message: `${warningClass.message} Affected ${warningClass.label}: ${entries.length}.${exampleText}`,
      }
    })
}

const WARNING_CODE_TITLES: Record<string, string> = Object.values(WARNING_CLASSES).reduce(
  (acc, warningClass) => ({
    ...acc,
    [warningClass.code]: warningClass.label,
  }),
  {
    'xlsx-chart-import-partial': 'Partial chart import',
    'xlsx-chart-unsupported': 'Unsupported chart detail',
    'xlsx-chart-source-unresolved': 'Unresolved chart source',
  } as Record<string, string>,
)

const titleFromWarningCode = (code: string): string => {
  const known = WARNING_CODE_TITLES[code]
  if (known) return known
  return code
    .replace(/^xlsx-/, '')
    .split(/[-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ') || 'Excel import warning'
}

export const buildSpreadsheetExcelIoWarningPresentation = (
  warning: SpreadsheetExcelIoWarning,
): SpreadsheetExcelIoWarningPresentation => {
  const code = String(warning.code || 'excel-import-warning').trim() || 'excel-import-warning'
  const detail = String(warning.message || code).trim() || code
  const scopeParts = [
    warning.sheet_name ? `Sheet: ${warning.sheet_name}` : '',
    warning.part_name ? `Part: ${warning.part_name}` : '',
  ].filter(Boolean)
  return {
    code,
    severity: warning.severity,
    title: titleFromWarningCode(code),
    detail,
    scope: scopeParts.length > 0 ? scopeParts.join(' | ') : null,
  }
}

export const buildSpreadsheetExcelIoWarningHistoryEntry = (options: {
  operation: 'import' | 'export'
  file_name: string
  warnings: SpreadsheetExcelIoWarning[]
  created_at_ms?: number
}): SpreadsheetExcelIoWarningHistoryEntry | null => {
  if (!Array.isArray(options.warnings) || options.warnings.length === 0) return null
  return {
    operation: options.operation,
    file_name: String(options.file_name || 'workbook.xlsx').trim() || 'workbook.xlsx',
    created_at_ms: Number.isFinite(Number(options.created_at_ms))
      ? Math.max(0, Math.floor(Number(options.created_at_ms)))
      : Date.now(),
    warning_count: options.warnings.length,
    warnings: options.warnings.map(buildSpreadsheetExcelIoWarningPresentation),
  }
}

const isWarningSeverity = (value: unknown): value is SpreadsheetExcelIoWarning['severity'] => (
  value === 'info' ||
  value === 'warning' ||
  value === 'error'
)

const normalizeWarningPresentation = (value: unknown): SpreadsheetExcelIoWarningPresentation | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  const code = String(record.code || '').trim()
  const title = String(record.title || '').trim()
  const detail = String(record.detail || '').trim()
  const severity = record.severity
  if (!code || !title || !detail || !isWarningSeverity(severity)) return null
  const scope = record.scope == null ? null : String(record.scope || '').trim() || null
  return { code, severity, title, detail, scope }
}

export const normalizeSpreadsheetExcelIoWarningHistoryEntries = (
  value: unknown,
  limit = SPREADSHEET_EXCEL_IO_WARNING_HISTORY_LIMIT,
): SpreadsheetExcelIoWarningHistoryEntry[] => {
  if (!Array.isArray(value)) return []
  const maxEntries = Math.max(0, Math.floor(Number(limit) || 0))
  if (maxEntries <= 0) return []
  const entries: SpreadsheetExcelIoWarningHistoryEntry[] = []
  for (const entryRaw of value) {
    if (!entryRaw || typeof entryRaw !== 'object' || Array.isArray(entryRaw)) continue
    const entry = entryRaw as Record<string, unknown>
    const operation = entry.operation === 'export' ? 'export' : entry.operation === 'import' ? 'import' : null
    const fileName = String(entry.file_name || '').trim()
    const createdAtMs = Number(entry.created_at_ms ?? 0)
    const warnings = Array.isArray(entry.warnings)
      ? entry.warnings.map(normalizeWarningPresentation).filter((warning): warning is SpreadsheetExcelIoWarningPresentation => Boolean(warning))
      : []
    if (!operation || !fileName || !Number.isFinite(createdAtMs) || createdAtMs <= 0 || warnings.length === 0) continue
    entries.push({
      operation,
      file_name: fileName,
      created_at_ms: Math.floor(createdAtMs),
      warning_count: Math.max(warnings.length, Math.floor(Number(entry.warning_count) || 0)),
      warnings,
    })
  }
  return entries
    .sort((a, b) => b.created_at_ms - a.created_at_ms)
    .slice(0, maxEntries)
}
